import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import SerialNumbersModal from "../../../../TablePro/components/assets/SerialNumbersModal";
import SerialNumbersList from "../../../../TablePro/components/assets/SerialNumbersList";

const styles = {
  dialogTitle: {
    position: "relative",
  },
  closeIcon: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  contentBox: {
    mt: 1,
  },
  dialogActions: {
    px: 3,
    py: 2,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 1,
  },
};

export default function BulkDuplicateModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  progress,
  showBarcodeOption = false,
  serialNumberEnabled = false,
  shipmentSerialNumbers = [],
}) {
  const [quantity, setQuantity] = useState();
  const [incrementBarcode, setIncrementBarcode] = useState(false);
  const [showSerialNumbersModal, setShowSerialNumbersModal] = useState(false);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const isConfirmDisabled = loading || (quantity || 0) <= 0;

  const handleQuantityChange = event => {
    setQuantity(event.target.value);
  };

  const handleBarcodeOptionChange = event => {
    setIncrementBarcode(event.target.checked);
  };

  const handleConfirm = () => {
    onConfirm(quantity, { incrementBarcode, serialNumbers });
  };

  const handleClose = () => {
    setQuantity(1);
    setIncrementBarcode(false);
    setSerialNumbers([]);
    onClose();
  };

  const handleSerialNumbersSave = serials => {
    setSerialNumbers(serials);
    setQuantity(serials.length + 1);
    setShowSerialNumbersModal(false);
  };

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography fontSize={20} color="text.primary" align="center">
          Bulk Duplicate Item
        </Typography>
        <IconButton onClick={handleClose} disabled={loading} sx={styles.closeIcon}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={styles.contentBox}>
          <TextField
            autoFocus
            label="Total Quantity Including This Item"
            type="number"
            fullWidth
            value={quantity}
            onChange={handleQuantityChange}
            disabled={loading}
            inputProps={{ min: 1 }}
          />
          {showBarcodeOption && (
            <Box mt={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incrementBarcode}
                    onChange={handleBarcodeOptionChange}
                    disabled={loading}
                  />
                }
                label="Increment barcode by +1 for each item"
                sx={{ color: "text.secondary" }}
              />
            </Box>
          )}
        </Box>
        {serialNumberEnabled && (
          <Box mt={1}>
            <SerialNumbersList serials={serialNumbers} />
          </Box>
        )}
        {loading && progress && (
          <Box mt={3} width="100%">
            <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
              Created {progress.current} items out of {progress.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        {serialNumberEnabled && (
          <Button
            variant="outlined"
            disabled={quantity <= 0 || !quantity}
            onClick={() => setShowSerialNumbersModal(true)}
          >
            Add Serial Numbers
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isConfirmDisabled}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Create Duplicates
        </Button>
      </DialogActions>
      {showSerialNumbersModal && (
        <SerialNumbersModal
          open={showSerialNumbersModal}
          onClose={() => setShowSerialNumbersModal(false)}
          quantity={quantity - 1}
          initialSerialNumbers={serialNumbers}
          existingShipmentSerialNumbers={shipmentSerialNumbers}
          onSave={handleSerialNumbersSave}
        />
      )}
    </Dialog>
  );
}
